/**
 * The Classic Postings status model (UL-7974).
 *
 * Every booked classic line resolves to one of three states from two dates: the booked start and
 * the date the poster went up. The posting window is a control on the page, so the status is
 * derived here on the client and never sent to or stored by the API -- changing the window
 * re-derives everything without a refetch.
 */

import type { ElasticSearchFilters } from 'c-sdk';
import { EsPCAReportFilterField } from 'c-sdk';

export enum PostingStatus {
    Within = 'in',
    Out = 'out',
    Awaiting = 'wait',
}

/** Posting window options in days; `0` is "on the booked date". */
export const POSTING_WINDOW_OPTIONS = [0, 1, 2, 3, 5, 7, 10] as const;
export const DEFAULT_POSTING_WINDOW = 5;

export type PostingLine = {
    displayId: string;
    frameId: string;
    name: string;
    owner: string;
    ownerId?: string;
    /** Market (DMA) -- `display_town` in the CAD. */
    market: string;
    /** State -- `display_tv_region` in the CAD. */
    state: string;
    format: string;
    postcode?: string;
    /** yyyy-MM-dd */
    bookedStart: string | null;
    /** yyyy-MM-dd, null while awaiting data. */
    postedUp: string | null;
    /** Whole days from booked start to poster up; null while awaiting data. */
    daysLate: number | null;
    /** Null while awaiting data -- never a zero dressed as a measurement. */
    impressions: number | null;
    expected: number | null;
    /** Impressions against expected, as a percentage. */
    pct: number | null;
};

/**
 * A booked classic line with no CAD rows at all. These exist only on the booked schedule, so they
 * come from their own source (UL-7975) rather than the report response.
 */
export type AwaitingPostingLine = Pick<
    PostingLine,
    | 'displayId'
    | 'frameId'
    | 'name'
    | 'owner'
    | 'ownerId'
    | 'market'
    | 'state'
    | 'format'
    | 'postcode'
    | 'bookedStart'
>;

/** A leaf row of the report response when grouped by display and day. */
export type PostingReportRow = {
    display_id?: string;
    playout_start_date?: string;
    frame_id?: string;
    display_screen_name_summary?: string;
    display_owner?: string;
    display_owner_id?: string | number;
    display_town?: string;
    display_tv_region?: string;
    display_format?: string;
    display_count?: number;
    sum_of_plays?: number | string;
    expected_impacts_in_schedule?: number | string;
    impacts_received_in_schedule?: number | string;
};

/**
 * The report nests one level per group-by field, in request order. The field order is irrelevant
 * here: every leaf carries its own group-by values, so collect the leaves and read those.
 */
export const collectReportRows = (node: unknown, out: PostingReportRow[] = []) => {
    if (node == null || typeof node !== 'object') return out;
    if ('display_count' in (node as Record<string, unknown>)) {
        out.push(node as PostingReportRow);
        return out;
    }
    Object.values(node as Record<string, unknown>).forEach(child => collectReportRows(child, out));
    return out;
};

const num = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const toUtcDay = (s: string) => {
    const [y, m, d] = s.slice(0, 10).split('-').map(Number);
    return Date.UTC(y, m - 1, d);
};

/** Whole days from `from` to `to`, both yyyy-MM-dd. */
export const diffDays = (from: string, to: string) =>
    Math.round((toUtcDay(to) - toUtcDay(from)) / 864e5);

const earlier = (a: string | null, b: string) => (a == null || b < a ? b : a);
const later = (a: string | null, b: string) => (a == null || b > a ? b : a);

/**
 * Builds one line per unit from per-unit, per-day report rows.
 *
 * - booked start: the first day with expected impacts. Expected *plays* are cast to whole numbers
 *   per row by the API, so a fractional first day can round to 0 -- impacts keep 4dp.
 * - poster up: the first day with any play.
 * - Delivery figures are impacts only. Plays are rounded per row by the API and a classic unit
 *   logs 0.99984 plays a day, so summing them here would drift the day counts.
 */
export const buildPostingLines = (
    rows: PostingReportRow[],
    awaiting: AwaitingPostingLine[] = [],
): { lines: PostingLine[]; dataThrough: string | null } => {
    const units = new Map<string, PostingLine & { hasRows: true }>();
    let dataThrough: string | null = null;

    rows.forEach(row => {
        const id = row.display_id == null ? '' : String(row.display_id);
        const day = row.playout_start_date?.slice(0, 10);
        if (!id || !day) return;

        let unit = units.get(id);
        if (!unit) {
            unit = {
                hasRows: true,
                displayId: id,
                frameId: row.frame_id ?? '',
                name: row.display_screen_name_summary ?? '',
                owner: row.display_owner ?? '',
                ownerId: row.display_owner_id == null ? undefined : String(row.display_owner_id),
                market: row.display_town ?? '',
                state: row.display_tv_region ?? '',
                format: row.display_format ?? '',
                bookedStart: null,
                postedUp: null,
                daysLate: null,
                impressions: 0,
                expected: 0,
                pct: null,
            };
            units.set(id, unit);
        }

        const expected = num(row.expected_impacts_in_schedule);
        const plays = num(row.sum_of_plays);
        if (expected > 0) unit.bookedStart = earlier(unit.bookedStart, day);
        if (plays > 0) {
            unit.postedUp = earlier(unit.postedUp, day);
            dataThrough = later(dataThrough, day);
        }
        unit.impressions += num(row.impacts_received_in_schedule);
        unit.expected += expected;
    });

    const lines: PostingLine[] = [...units.values()].map(({ hasRows, ...unit }) => {
        if (unit.postedUp == null) {
            // In the CAD but nothing has played yet: awaiting. Its expected impacts are real -- the
            // schedule says what it should have delivered -- so they stay, and "Expected to date"
            // matches the Classic report. Delivered, % and days late are not measured, so they
            // stay null rather than a zero dressed as a measurement.
            return { ...unit, impressions: null, pct: null, daysLate: null };
        }
        return {
            ...unit,
            daysLate: unit.bookedStart ? diffDays(unit.bookedStart, unit.postedUp) : null,
            pct: unit.expected ? (unit.impressions / unit.expected) * 100 : null,
        };
    });

    awaiting.forEach(line => {
        if (units.has(String(line.displayId))) return;
        lines.push({
            ...line,
            displayId: String(line.displayId),
            postedUp: null,
            daysLate: null,
            impressions: null,
            expected: null,
            pct: null,
        });
    });

    return { lines, dataThrough };
};

export const postingStatus = (line: PostingLine, windowDays: number): PostingStatus => {
    if (line.postedUp == null) return PostingStatus.Awaiting;
    return (line.daysLate ?? 0) <= windowDays ? PostingStatus.Within : PostingStatus.Out;
};

export type StatusedPostingLine = PostingLine & { status: PostingStatus };

export const withStatus = (lines: PostingLine[], windowDays: number): StatusedPostingLine[] =>
    lines.map(line => ({ ...line, status: postingStatus(line, windowDays) }));

export type StatusCounts = Record<PostingStatus, number>;

export const countStatuses = (lines: StatusedPostingLine[]): StatusCounts => {
    const c: StatusCounts = { in: 0, out: 0, wait: 0 };
    lines.forEach(l => {
        c[l.status] += 1;
    });
    return c;
};

/** Awaiting lines are missing data, not a compliance failure, so they never enter these. */
export const daysLateStats = (lines: PostingLine[]) => {
    const reporting = lines.filter(l => l.postedUp != null && l.daysLate != null);
    if (!reporting.length) return { avg: null as number | null, worst: null as number | null };
    return {
        // Posting early is not credit against another unit posting late.
        avg: reporting.reduce((s, l) => s + Math.max(0, l.daysLate), 0) / reporting.length,
        worst: Math.max(...reporting.map(l => l.daysLate)),
    };
};

export type PostingBreakdownRow = {
    key: string;
    total: number;
    counts: StatusCounts;
    avg: number | null;
    worst: number | null;
};

export const postingBreakdown = (
    lines: StatusedPostingLine[],
    key: 'owner' | 'state' | 'market',
): PostingBreakdownRow[] => {
    const groups = new Map<string, StatusedPostingLine[]>();
    lines.forEach(l => {
        const k = l[key] || '';
        groups.set(k, [...(groups.get(k) ?? []), l]);
    });
    return [...groups.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, group]) => ({
            key: k,
            total: group.length,
            counts: countStatuses(group),
            ...daysLateStats(group),
        }));
};

export type PostingFigures = {
    delivered: number;
    expected: number;
    variance: number;
    pctDelivered: number;
    unitsUp: number;
    units: number;
};

export const postingFigures = (lines: StatusedPostingLine[]): PostingFigures => {
    const delivered = lines.reduce((s, l) => s + (l.impressions ?? 0), 0);
    const expected = lines.reduce((s, l) => s + (l.expected ?? 0), 0);
    const unitsUp = lines.filter(l => l.status !== PostingStatus.Awaiting).length;
    return {
        delivered,
        expected,
        variance: delivered - expected,
        pctDelivered: expected ? (delivered / expected) * 100 : 0,
        unitsUp,
        units: lines.length,
    };
};

export type PostingSortKey =
    | 'name'
    | 'owner'
    | 'market'
    | 'state'
    | 'bookedStart'
    | 'postedUp'
    | 'daysLate'
    | 'impressions'
    | 'pct';

export type PostingSort = { key: PostingSortKey; dir: 1 | -1 };

export const DEFAULT_POSTING_SORT: PostingSort = { key: 'daysLate', dir: -1 };

/** Text columns start ascending, numbers and dates start with the biggest first. */
export const initialSortDir = (key: PostingSortKey): 1 | -1 =>
    ['name', 'owner', 'market', 'state'].includes(key) ? 1 : -1;

/** Awaiting lines sort to the bottom whichever way the column is sorted. */
export const sortPostingLines = (lines: StatusedPostingLine[], { key, dir }: PostingSort) =>
    [...lines].sort((a, b) => {
        const aWait = a.status === PostingStatus.Awaiting;
        const bWait = b.status === PostingStatus.Awaiting;
        if (aWait !== bWait) return aWait ? 1 : -1;

        const x = a[key];
        const y = b[key];
        if (x == null && y == null) return 0;
        if (x == null) return 1;
        if (y == null) return -1;
        const cmp =
            typeof x === 'string' ? x.localeCompare(String(y)) : (x as number) - (y as number);
        return cmp * dir;
    });

/** The report filters an awaiting line can be matched on; it has no CAD row for the rest. */
export const AWAITING_FILTER_FIELDS: Partial<
    Record<EsPCAReportFilterField, keyof AwaitingPostingLine>
> = {
    [EsPCAReportFilterField.DisplayOwner]: 'owner',
    [EsPCAReportFilterField.DisplayTown]: 'market',
    [EsPCAReportFilterField.DisplayTvRegion]: 'state',
    [EsPCAReportFilterField.DisplayFormat]: 'format',
    [EsPCAReportFilterField.FrameId]: 'frameId',
};

/** Whether a line passes the report filters, matched on the unit's own details. */
export const matchesPostingFilters = (line: PostingLine, fieldFilters: ElasticSearchFilters) =>
    Object.entries(fieldFilters).every(([field, values]) => {
        if (!values?.length) return true;
        const key = AWAITING_FILTER_FIELDS[field as EsPCAReportFilterField];
        return key != null && values.map(String).includes(String(line[key] ?? ''));
    });

/** Drops filters with no values selected, so they neither narrow nor change the cache key. */
export const activePostingFilters = (fieldFilters: ElasticSearchFilters): ElasticSearchFilters =>
    Object.fromEntries(Object.entries(fieldFilters ?? {}).filter(([, v]) => v?.length));
